import supabase from '../lib/supabase'

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png']
export const MAX_AVATAR_SIZE_BYTES = 15 * 1024 * 1024 // 15MB

function getFileExtensionFromMime(mimeType: string): string {
  if (mimeType === 'image/jpeg') return 'jpg'
  if (mimeType === 'image/png') return 'png'
  return 'bin'
}

function generateUuid(): string {
  // @ts-ignore
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    // @ts-ignore
    return crypto.randomUUID()
  }
  // fallback simples
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export async function getSignedAvatarUrl(path?: string | null, expiresInSeconds = 3600): Promise<string | null> {
  if (!path) return null

  const { data, error } = await supabase.storage.from('avatars').createSignedUrl(path, expiresInSeconds)
  if (error) {
    console.error('Erro ao gerar URL assinada do avatar:', error.message)
    return null
  }
  return data?.signedUrl ?? null
}

export async function uploadAvatar(params: {
  file: File
  userId: string
  oldPath?: string | null
}): Promise<{ path: string }> {
  const { file, userId, oldPath } = params

  // Validações
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Tipo de arquivo inválido. Use JPEG ou PNG.')
  }
  if (file.size > MAX_AVATAR_SIZE_BYTES) {
    throw new Error('Arquivo muito grande. Tamanho máximo: 15MB.')
  }

  const ext = getFileExtensionFromMime(file.type)
  const newPath = `${userId}/${generateUuid()}.${ext}`

  // Upload
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(newPath, file, {
      upsert: true,
      contentType: file.type
    })

  if (uploadError) {
    console.error('Erro no upload do avatar:', uploadError.message)
    throw new Error('Falha ao enviar o avatar. Tente novamente.')
  }

  // Atualizar perfil
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ avatar_url: newPath, updated_at: new Date().toISOString() })
    .eq('id', userId)

  if (updateError) {
    console.error('Erro ao salvar avatar no perfil:', updateError.message)
    // Tentar limpar o arquivo recém enviado para não deixar lixo
    try { await supabase.storage.from('avatars').remove([newPath]) } catch {}
    throw new Error('Falha ao atualizar seu perfil com o novo avatar.')
  }

  // Remover antigo (opcional)
  if (oldPath && oldPath !== newPath) {
    try {
      await supabase.storage.from('avatars').remove([oldPath])
    } catch (e) {
      // Log e segue; remoção não é crítica para o fluxo do usuário
      console.warn('Não foi possível remover o avatar antigo:', (e as Error)?.message)
    }
  }

  return { path: newPath }
}

/** Remove o arquivo de avatar do storage. O perfil deve ser atualizado separadamente (ex.: updateProfile({ avatar_url: null })). */
export async function removeAvatarFile(oldPath: string | null): Promise<void> {
  if (!oldPath) return
  try {
    await supabase.storage.from('avatars').remove([oldPath])
  } catch (e) {
    console.warn('Não foi possível remover o arquivo do avatar:', (e as Error)?.message)
  }
}
